import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, Observable, Subject, debounceTime, Subscription } from 'rxjs';
import { Cart, CartItem, CartValidation, AusPostShippingService, IShippingAddress  } from '@redirector-service/shared';
import { environment } from '../environments/environment';
import { Stripe } from '@stripe/stripe-js';
import { NGXLogger } from 'ngx-logger';

declare const Stripe: (publishableKey: string) => Stripe;

@Injectable({
  providedIn: 'root'
})
export class CartService implements OnDestroy {
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);


    /* 
    Future use, this could be set from environment to access the backend API 
    on different hosts if required. For now it's just an empty string to assume 
    the same host name. See proxy.conf.json for the proxy configuration while
    developing the application.
  */
  private static API_HOST = environment.apiHost || '';


  private readonly STORAGE_KEY = 'cart';
  private cartSubject = new BehaviorSubject<Cart>({ items: [], totalAmount: 0 });

  private readonly ITEMS_JWT_KEY = 'items-jwt';
  private storageEventSubject = new Subject<StorageEvent>();
  private storageSubscription?: Subscription;

  readonly stripe = Stripe(environment.stripePublishableKey);

  // Storage event listener for cross-tab synchronization
  private storageListener = (event: StorageEvent) => {
    if (event.key === this.STORAGE_KEY) {
      this.storageEventSubject.next(event);
    }
  };

  constructor() {
    this.loadCart();
    this.setupStorageListener();
    this.logger.log('CartService initialized');
  }

  private loadCart() {
    const cart = localStorage.getItem(this.STORAGE_KEY);
    if (cart) {
      this.cartSubject.next(JSON.parse(cart));
      this.logger.log('Cart loaded from localStorage', JSON.parse(cart));
    }
  }

  private setupStorageListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.storageListener);
      this.logger.log('Storage event listener added for cross-tab synchronization');
      
      // Set up debounced subscription to handle storage events
      this.storageSubscription = this.storageEventSubject.pipe(
        debounceTime(100)
      ).subscribe((event: StorageEvent) => {
        try {
          if (event.newValue) {
            const updatedCart = JSON.parse(event.newValue);
            this.cartSubject.next(updatedCart);
            this.logger.log('Cart synchronized from another tab', updatedCart);
          } else {
            // Cart was cleared in another tab
            this.cartSubject.next({ items: [], totalAmount: 0 });
            this.logger.log('Cart cleared from another tab');
          }
        } catch (error) {
          this.logger.error('Error parsing cart data from storage event', error);
        }
      });
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
      this.logger.log('Storage event listener removed');
    }
    
    // Clean up subscription to prevent memory leaks
    if (this.storageSubscription) {
      this.storageSubscription.unsubscribe();
      this.storageSubscription = undefined;
      this.logger.log('Storage subscription unsubscribed');
    }
    
    // Complete the subject to clean up any remaining observers
    this.storageEventSubject.complete();
  }

  private saveCart(cart: Cart) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    this.cartSubject.next(cart);
    this.logger.log('Cart saved to localStorage', cart);
  }

  getCart(): Observable<Cart> {
    this.logger.log('getCart called');
    return this.cartSubject.asObservable();
  }

  /**
   * Returns an Observable that emits the total quantity of all items in the cart.
   * For example, if the cart contains 2 of item A and 3 of item B, it will emit 5.
   * @returns Observable<number> The total quantity of all items
   */
  getCartItemCount(): Observable<number> {
    this.logger.log('getCartItemCount called');
    return this.cartSubject.asObservable().pipe(
      map(cart => {
        const itemCount = cart.items.reduce((acc, item) => acc + item.quantity, 0);
        this.logger.log('Cart item count:', itemCount);
        return itemCount;
      })
    );
  }

  addToCart(item: CartItem) {
    this.logger.log('addToCart called with item', item);
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      shippingAddress: this.cartSubject.value.shippingAddress,
      totalAmount: 0
    };
    const existingItem = cart.items.find(i => i.id === item.id);
    if (existingItem) {
      existingItem.quantity += item.quantity;
      this.logger.log('Updated existing item quantity', existingItem);
    } else {
      cart.items.push({...item});
      this.logger.log('Added new item to cart', item);
    }
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  setCartItem(item: CartItem) {
    this.logger.log('setCartItem called with item', item);
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      shippingAddress: this.cartSubject.value.shippingAddress,
      totalAmount: 0
    };
    const existingItem = cart.items.find(i => i.id === item.id);
    if (existingItem) {
      existingItem.quantity = item.quantity;
      this.logger.log('Updated existing item quantity', existingItem);
    } else {
      cart.items.push({...item});
      this.logger.log('Added new item to cart', item);
    }
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  removeFromCart(item: CartItem) {
    this.logger.log('removeFromCart called with item', item);
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      shippingAddress: this.cartSubject.value.shippingAddress,
      totalAmount: 0
    };
    const existingItem = cart.items.find(i => i.id === item.id);
    if (existingItem) {
      existingItem.quantity -= item.quantity;
      this.logger.log('Decreased item quantity', existingItem);
      if (existingItem.quantity <= 0) {
        cart.items = cart.items.filter(i => i.id !== item.id);
        this.logger.log('Removed item from cart', item);
      }
    }
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  setShippingService(service: AusPostShippingService) {
    this.logger.log('setShippingService called with service', service);
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      totalAmount: this.cartSubject.value.totalAmount,
      shippingAddress: this.cartSubject.value.shippingAddress,
      shippingService: service
    };
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  clearShippingService() {
    this.logger.log('clearShippingService called');
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      totalAmount: this.cartSubject.value.totalAmount,
      shippingAddress: this.cartSubject.value.shippingAddress,
    };
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  setShippingAddress(shippingAddress: IShippingAddress) {
    this.logger.log('setShippingAddress called with address', shippingAddress);
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      totalAmount: this.cartSubject.value.totalAmount,
      shippingAddress,
    };
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  clearShippingAddress() {
    this.logger.log('clearShippingAddress called');
    const cart: Cart = {
      items: [...this.cartSubject.value.items],
      totalAmount: this.cartSubject.value.totalAmount,
    };
    this.calculateTotalAmount(cart);
    this.saveCart(cart);
  }

  private calculateTotalAmount(cart: Cart): void {
    const cartTotal = cart.items.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const shippingCost = cart.shippingService?.price || 0;
    cart.totalAmount = cartTotal + shippingCost;
    this.logger.log('Total amount calculated', cart.totalAmount);
    this.logger.log('Cart items cost:', cartTotal);
    this.logger.log('Cart shipping calculation', cart.shippingService?.price);
  }

  clearCart() {
    this.logger.log('clearCart called');
    this.saveCart({ 
      items: [], 
      totalAmount: 0,
      shippingAddress: this.cartSubject.value.shippingAddress,
    });
  }

  validateCart(): Observable<CartValidation> {
    this.logger.log('validateCart called');
    const cart = this.cartSubject.value;
    return this.http.post<CartValidation>(`${CartService.API_HOST}/api/cart/validate`, cart);
  }

  getAvailableShippingServices(): Observable<AusPostShippingService[]> {
    this.logger.log('getAvailableShippingServices called');
    const cart = this.cartSubject.value;
    this.logger.log('Cart for shipping services:', cart);
    if (!cart.shippingAddress) {
      this.logger.log('No shipping address found in cart');
      return new Observable<AusPostShippingService[]>(observer => {
        observer.error('No shipping address found in cart');
      });
    }
    return this.http.post<AusPostShippingService[]>(`${CartService.API_HOST}/api/shipping/services`, cart);
  }

  createCheckoutSession(): Observable<{ sessionId: string }> {
    this.logger.log('createCheckoutSession called');
    return this.http.post<{ sessionId: string }>(`${CartService.API_HOST}/api/checkout/session`, this.cartSubject.value);
  }

  async initialiseEmbeddedCheckout(sessionId: string) {
    this.logger.log('initialiseEmbeddedCheckout called with id:', sessionId);
    const checkout = await this.stripe.initEmbeddedCheckout({
      clientSecret: sessionId,
    });
    return checkout;
  }

  retrieveCheckoutSession(sessionId: string): Observable<any> {
    this.logger.log('retrieveCheckoutSession called with id:', sessionId);
    return this.http.get<any>(`${CartService.API_HOST}/api/checkout/session/${sessionId}`);
  }

  verifyShippingAddress(address: any): Observable<any> {
    this.logger.log('verifyShippingAddress called with address:', address);
    return this.http.post<any>(`${CartService.API_HOST}/api/shipping/address/validate`, address);
  }

  getShippingAddress() {
    // this.logger.log('getShippingAddress called', Date());
    return this.cartSubject.value.shippingAddress;
  }

  getShippingService() {
    // this.logger.log('getShippingService called');
    return this.cartSubject.value.shippingService;
  }

  // createLineItemsForCart(): Observable<CartJWT> {
  //   this.logger.log('createLineItemsForCart called');
  //   const cart = this.cartSubject.value;
  //   return this.http.post<CartJWT>(`${CartService.API_HOST}/api/cart/items`, cart);
  // }

  // saveLineItemsJWT(jwt: string) {
  //   this.logger.log('saveLineItemsJWT called with jwt', jwt);
  //   localStorage.setItem(this.ITEMS_JWT_KEY, jwt);
  // }

  // loadLineItemsJWT(): string | null {
  //   this.logger.log('loadLineItemsJWT called');
  //   const jwt = localStorage.getItem(this.ITEMS_JWT_KEY);
  //   if (!jwt) {
  //     this.logger.log('No JWT found in localStorage');
  //     return null;
  //   }
  //   return jwt;
  // }
}
